import {
  DownOutlined,
  EyeOutlined,
  QuestionCircleOutlined,
  RightOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import {
  Button,
  DatePicker,
  InputNumber,
  Modal,
  Select,
  Spin,
  Tabs,
  Tooltip,
  message,
} from "antd";
import {
  ConceptDropSelect,
  StockLink,
  StockPrice,
  ratioToStockPricePct,
  type ConceptSearchBrief,
  type StockNotesTabProps,
  type StockSearchBrief,
} from "fin-web-components";
import { KChart, type KLineData } from "kchart";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SearchStockWithCatalog } from "../../components/fin-web/SearchStockWithCatalog";
import type { StockLinkDetailState } from "../../components/fin-web/useStockLinkDetail";
import { useStockLinkDetail, stockLinkDetailPropsForCode } from "../../components/fin-web/useStockLinkDetail";
import { globalModel } from "../../global/GlobalModel";
import { TacticsPageUiConfig, tacticsPageCssVars, tacticsWidthCss } from "./Config";
import {
  buildTacticsKlineData,
  findTacticsAssessForKlineTime,
} from "./buildTacticsKlineData";
import { TacticsApi } from "./TacticsApi";
import type {
  TacticsDailyScorePointDTO,
  TacticsSelectableConceptDTO,
  TacticsSignalIconOptionDTO,
  TacticsStockKlineBarDTO,
  TacticsStockScoreRowDTO,
  TacticsStrategyBriefDTO,
  TacticsStrategyScoreType,
  TacticsChanceSidePreference,
  TacticsUserSignalSettingsDTO,
} from "./TacticsTypes";
import {
  getRecentStrategyIds,
  recordLocalStrategyVisit,
  sortStrategiesByRecentVisit,
  TACTICS_DROPDOWN_CAP,
} from "./tacticsRecentStrategies";
import "./TacticsPage.css";

dayjs.extend(customParseFormat);

const RECT_BTN = "tactics-rect-modal-btn";
const PRICE_DEBUG_CLICKS = 5;
const PRICE_CLICK_RESET_MS = 2500;

function tacticsApiDateTimeString(v: unknown): string {
  if (v == null || v === "") {
    return "";
  }
  if (typeof v === "string") {
    return v.trim();
  }
  if (typeof v === "number" && Number.isFinite(v)) {
    return dayjs(v).format("YYYY-MM-DD HH:mm:ss");
  }
  if (v instanceof Date) {
    return dayjs(v).format("YYYY-MM-DD HH:mm:ss");
  }
  return String(v).trim();
}

function parseTacticsDataDate(raw: unknown): dayjs.Dayjs | null {
  const s = tacticsApiDateTimeString(raw);
  if (!s) return null;
  const d = dayjs(s, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD"], true);
  return d.isValid() ? d : null;
}

function toTacticsDataDateApi(d: dayjs.Dayjs): string {
  return `${d.format("YYYY-MM-DD")} 15:00:00`;
}

function stockRowKey(r: TacticsStockScoreRowDTO): string {
  return r.stockCode?.trim() || "";
}

function sortTacticsRowsByScoreDesc(rows: TacticsStockScoreRowDTO[]): TacticsStockScoreRowDTO[] {
  return [...rows].sort((a, b) => {
    const scoreA = Number.isFinite(a.score) ? a.score : -Infinity;
    const scoreB = Number.isFinite(b.score) ? b.score : -Infinity;
    return scoreB - scoreA;
  });
}

function stockRowDisplayName(r: TacticsStockScoreRowDTO): string {
  const code = stockRowKey(r);
  return r.stockName?.trim() || code;
}

function normalizeStrategyScoreType(raw: string | undefined): TacticsStrategyScoreType {
  const v = raw?.trim().toUpperCase();
  if (v === "RISK") {
    return "RISK";
  }
  if (v === "ALL") {
    return "ALL";
  }
  return "CHANCE";
}

function normalizeChanceSidePreference(raw: string | undefined): TacticsChanceSidePreference {
  const v = raw?.trim().toUpperCase();
  if (v === "LEFT") {
    return "LEFT";
  }
  if (v === "RIGHT") {
    return "RIGHT";
  }
  return "BOTH";
}

function formatStrategySideSuffix(pref: TacticsChanceSidePreference): string {
  if (pref === "LEFT") {
    return "（左侧策略）";
  }
  if (pref === "RIGHT") {
    return "（右侧策略）";
  }
  return "（双侧策略）";
}

function formatStrategySelectLabel(name: string, sidePref: TacticsChanceSidePreference): string {
  const n = name.trim() || "未命名策略";
  return `${n}${formatStrategySideSuffix(sidePref)}`;
}

function strategySupportsOpportunity(type: TacticsStrategyScoreType): boolean {
  return type === "CHANCE" || type === "ALL";
}

function strategySupportsRisk(type: TacticsStrategyScoreType): boolean {
  return type === "RISK" || type === "ALL";
}

function formatSignalLegendLabel(
  kind: "机会" | "风险",
  threshold: number | null | undefined
): string {
  if (typeof threshold === "number" && Number.isFinite(threshold)) {
    return `${kind}(${Math.round(threshold)})`;
  }
  return kind;
}

function hasConfiguredSignalForStrategy(
  settings: TacticsUserSignalSettingsDTO | null,
  strategyType: TacticsStrategyScoreType
): boolean {
  if (!settings) {
    return false;
  }
  const oppConfigured =
    !!settings.opportunityIconUrl?.trim() &&
    typeof settings.opportunityThreshold === "number" &&
    Number.isFinite(settings.opportunityThreshold);
  const riskConfigured =
    !!settings.riskIconUrl?.trim() &&
    typeof settings.riskThreshold === "number" &&
    Number.isFinite(settings.riskThreshold);
  return (
    (strategySupportsOpportunity(strategyType) && oppConfigured) ||
    (strategySupportsRisk(strategyType) && riskConfigured)
  );
}

function readSavedSignalDraft(saved: TacticsUserSignalSettingsDTO | null | undefined): {
  opportunityThreshold: number | null;
  opportunityIconUrl: string | null;
  riskThreshold: number | null;
  riskIconUrl: string | null;
} {
  return {
    opportunityThreshold:
      typeof saved?.opportunityThreshold === "number" &&
      Number.isFinite(saved.opportunityThreshold)
        ? saved.opportunityThreshold
        : null,
    opportunityIconUrl: saved?.opportunityIconUrl?.trim() || null,
    riskThreshold:
      typeof saved?.riskThreshold === "number" && Number.isFinite(saved.riskThreshold)
        ? saved.riskThreshold
        : null,
    riskIconUrl: saved?.riskIconUrl?.trim() || null,
  };
}

function formatTacticsScoreValue(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) {
    return "—";
  }
  return v.toFixed(1);
}

function KlineScoreHintPair({ label, value }: { label: string; value: string }) {
  return (
    <>
      <span className="tactics-chart-hint-label">{label}</span>
      <span className="tactics-chart-hint-value">{value}</span>
    </>
  );
}

function buildAssessExplainText(
  assess: TacticsDailyScorePointDTO | null,
  strategyType: TacticsStrategyScoreType
): string | null {
  if (!assess) {
    return null;
  }
  const chance = assess.chanceExplain?.trim();
  const risk = assess.riskExplain?.trim();
  if (strategyType === "CHANCE") {
    return chance || null;
  }
  if (strategyType === "RISK") {
    return risk || null;
  }
  const parts: string[] = [];
  if (chance) {
    parts.push(`【机会】\n${chance}`);
  }
  if (risk) {
    parts.push(`【风险】\n${risk}`);
  }
  return parts.length ? parts.join("\n\n") : null;
}

function buildKlineScoreHintContent(
  assess: TacticsDailyScorePointDTO | null,
  strategyType: TacticsStrategyScoreType
): React.ReactNode {
  if (!assess) {
    return "该日暂无分数";
  }
  const opp = formatTacticsScoreValue(assess.score);
  const risk = formatTacticsScoreValue(assess.riskScore);
  if (strategyType === "CHANCE") {
    return <KlineScoreHintPair label="机会分" value={opp} />;
  }
  if (strategyType === "RISK") {
    return <KlineScoreHintPair label="风险分" value={risk} />;
  }
  return (
    <>
      <KlineScoreHintPair label="机会分" value={opp} />
      <span className="tactics-chart-hint-sep"> / </span>
      <KlineScoreHintPair label="风险分" value={risk} />
    </>
  );
}

export interface TacticsPageProps {
  spaceId: string;
  spaceProductId: string;
}

const TacticsPage: React.FC<TacticsPageProps> = ({ spaceId, spaceProductId }) => {
  const [strategyLoading, setStrategyLoading] = useState(true);
  const [strategyId, setStrategyId] = useState<string>("");
  const strategyInitRef = useRef(false);

  useEffect(() => {
    strategyInitRef.current = false;
  }, [spaceProductId]);
  const [marketStrategies, setMarketStrategies] = useState<TacticsStrategyBriefDTO[]>(
    []
  );

  const [marketOpen, setMarketOpen] = useState(false);
  const [marketPick, setMarketPick] = useState<string>("");

  const [leftTab, setLeftTab] = useState<
    "top" | "watchlist" | "holdings" | "concept"
  >("top");
  const [listLoading, setListLoading] = useState(false);
  const [topPayload, setTopPayload] = useState<{
    dataDate: string;
    rows: TacticsStockScoreRowDTO[];
  }>({ dataDate: "", rows: [] });
  /** 高分榜查询日；空串表示取最新评估日 */
  const [topQueryDate, setTopQueryDate] = useState<string>("");
  const [watchRows, setWatchRows] = useState<TacticsStockScoreRowDTO[]>([]);
  const [holdRows, setHoldRows] = useState<TacticsStockScoreRowDTO[]>([]);
  const [conceptRows, setConceptRows] = useState<TacticsStockScoreRowDTO[]>([]);
  const [conceptConceptDtos, setConceptConceptDtos] = useState<
    TacticsSelectableConceptDTO[]
  >([]);
  const [tactikConceptCode, setTactikConceptCode] = useState<string>("");

  /** 左侧列表选中（与搜索框无关） */
  const [listSelectedStockCode, setListSelectedStockCode] = useState<string>("");
  const listSelectedStockCodeRef = useRef(listSelectedStockCode);
  listSelectedStockCodeRef.current = listSelectedStockCode;
  /** 左上搜索框选中（与左侧列表无关） */
  const [searchStockCode, setSearchStockCode] = useState<string>("");
  /** 右侧图表/详情展示的股票 */
  const [panelStockCode, setPanelStockCode] = useState<string>("");
  const panelStockCodeRef = useRef(panelStockCode);
  panelStockCodeRef.current = panelStockCode;
  const [pickedFromSearch, setPickedFromSearch] = useState(false);
  const [selectedKlineTime, setSelectedKlineTime] = useState<number | null>(null);
  const [debugMode, setDebugMode] = useState(false);
  const [assessExplainOpen, setAssessExplainOpen] = useState(false);
  const priceClickCountRef = useRef(0);
  const priceClickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const chartRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(0);
  const [chartHeight, setChartHeight] = useState(0);
  const [panelLoading, setPanelLoading] = useState(false);
  const [klineBars, setKlineBars] = useState<TacticsStockKlineBarDTO[]>([]);
  const [assessPoints, setAssessPoints] = useState<TacticsDailyScorePointDTO[]>([]);

  const [signalSettingsOpen, setSignalSettingsOpen] = useState(false);
  const [signalBusy, setSignalBusy] = useState(false);
  const [signalOpportunityOpts, setSignalOpportunityOpts] = useState<
    TacticsSignalIconOptionDTO[]
  >([]);
  const [signalRiskOpts, setSignalRiskOpts] = useState<TacticsSignalIconOptionDTO[]>(
    []
  );
  const [signalSaved, setSignalSaved] =
    useState<TacticsUserSignalSettingsDTO | null>(null);
  const [draftOppThr, setDraftOppThr] = useState<number | null>(null);
  const [draftRiskThr, setDraftRiskThr] = useState<number | null>(null);
  const [draftOppIcon, setDraftOppIcon] = useState<string | null>(null);
  const [draftRiskIcon, setDraftRiskIcon] = useState<string | null>(null);

  const { stockLinkDetail, requestStockDetailForLink, buildStockNotesTabProps } =
    useStockLinkDetail(spaceId);
  const requestStockDetailRef = useRef(requestStockDetailForLink);
  requestStockDetailRef.current = requestStockDetailForLink;

  const leftTabsIndicator = useMemo(
    () => ({ size: (origin: number) => origin, align: "center" as const }),
    []
  );

  useEffect(() => {
    const el = chartRef.current;
    if (!el) {
      return;
    }
    const ro = new ResizeObserver(([entry]) => {
      setChartWidth(entry.contentRect.width);
      setChartHeight(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const tacticsUserId = globalModel.currentUser?.id;

  /** 下拉：全量按本机最近访问排序后的前 N 条 */
  const dropdownItems = useMemo(
    () => marketStrategies.slice(0, TACTICS_DROPDOWN_CAP),
    [marketStrategies]
  );

  /** 全量策略按本机最近访问重排 */
  const applyStrategiesRecentOrder = useCallback(
    (rows: TacticsStrategyBriefDTO[]): TacticsStrategyBriefDTO[] => {
      const sorted = sortStrategiesByRecentVisit(
        spaceId,
        spaceProductId,
        rows,
        tacticsUserId
      );
      setMarketStrategies(sorted);
      return sorted;
    },
    [spaceId, spaceProductId, tacticsUserId]
  );

  const noteStrategyVisit = useCallback(
    (strategyIdRaw: string) => {
      const sid = strategyIdRaw.trim();
      if (!sid) return;
      recordLocalStrategyVisit(spaceId, spaceProductId, sid, tacticsUserId);
      void TacticsApi.recordStrategyVisit(spaceId, spaceProductId, sid);
      setMarketStrategies((prev) => {
        if (!prev.length) {
          return prev;
        }
        return sortStrategiesByRecentVisit(
          spaceId,
          spaceProductId,
          prev,
          tacticsUserId
        );
      });
    },
    [spaceId, spaceProductId, tacticsUserId]
  );

  useEffect(() => {
    let alive = true;
    setStrategyLoading(true);
    void (async () => {
      // 拉全量后按本机最近访问排序，下拉取前 5（与「最近优先」一致）
      const rows = await TacticsApi.listMarketStrategies(spaceId, spaceProductId);
      if (!alive) return;
      const list = Array.isArray(rows) ? rows : [];
      const sorted = applyStrategiesRecentOrder(list);
      if (!strategyInitRef.current && sorted.length) {
        let pick = sorted[0]!.id.trim();
        const localRecent = getRecentStrategyIds(spaceId, spaceProductId, tacticsUserId);
        // 本机无记录时，回退服务端默认策略
        if (!localRecent.length) {
          try {
            const def = await TacticsApi.resolveDefaultStrategy(spaceId, spaceProductId);
            if (def && sorted.some((r) => r.id.trim() === def)) {
              pick = def;
            }
          } catch {
            /* 使用排序首项 */
          }
        }
        setStrategyId(pick);
        strategyInitRef.current = true;
        noteStrategyVisit(pick);
      }
      setStrategyLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [
    applyStrategiesRecentOrder,
    noteStrategyVisit,
    spaceId,
    spaceProductId,
    tacticsUserId,
  ]);

  useEffect(() => {
    setTopQueryDate("");
  }, [strategyId]);

  useEffect(() => {
    let alive = true;
    void TacticsApi.listSelectableConcepts(spaceId, spaceProductId).then((rows) => {
      if (!alive) return;
      setConceptConceptDtos(rows);
    });
    return () => {
      alive = false;
    };
  }, [spaceId, spaceProductId]);

  useEffect(() => {
    if (!strategyId.trim()) return undefined;
    let alive = true;
    setListLoading(true);
    const sid = strategyId.trim();
    let p: Promise<unknown>;

    if (leftTab === "top") {
      const queryDate = topQueryDate.trim();
      p = TacticsApi.listTopScores(
        spaceId,
        spaceProductId,
        sid,
        queryDate || undefined
      ).then((r) => {
        if (!alive) return;
        setTopPayload({
          dataDate: tacticsApiDateTimeString(r.dataDate),
          rows: Array.isArray(r.rows) ? r.rows : [],
        });
      });
    } else if (leftTab === "watchlist") {
      p = TacticsApi.listWatchlistScores(spaceId, spaceProductId, sid).then((rows) => {
        if (!alive) return;
        setWatchRows(rows);
      });
    } else if (leftTab === "holdings") {
      p = TacticsApi.listHoldingsScores(spaceId, spaceProductId, sid).then((rows) => {
        if (!alive) return;
        setHoldRows(rows);
      });
    } else if (leftTab === "concept" && tactikConceptCode.trim()) {
      p = TacticsApi.listConceptStockScores(
        spaceId,
        spaceProductId,
        sid,
        tactikConceptCode.trim()
      ).then((rows) => {
        if (!alive) return;
        setConceptRows(rows);
      });
    } else {
      setListLoading(false);
      return () => {
        alive = false;
      };
    }

    void p.finally(() => {
      if (alive) setListLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [tactikConceptCode, leftTab, spaceId, spaceProductId, strategyId, topQueryDate]);

  const displayRows = useMemo(() => {
    if (leftTab === "top") return topPayload.rows;
    if (leftTab === "watchlist") return watchRows;
    if (leftTab === "holdings") return holdRows;
    if (leftTab === "concept") return sortTacticsRowsByScoreDesc(conceptRows);
    return [];
  }, [conceptRows, holdRows, leftTab, topPayload.rows, watchRows]);

  const displayRowCodesKey = useMemo(
    () => displayRows.map(stockRowKey).filter(Boolean).join("\u0001"),
    [displayRows]
  );

  useEffect(() => {
    if (pickedFromSearch) {
      return;
    }
    if (!displayRowCodesKey) {
      if (listSelectedStockCodeRef.current) {
        setListSelectedStockCode("");
      }
      if (panelStockCodeRef.current) {
        setPanelStockCode("");
      }
      return;
    }
    const codes = displayRowCodesKey.split("\u0001");
    const current = listSelectedStockCodeRef.current.trim();
    const nextCode = current && codes.includes(current) ? current : codes[0]!;
    const listChanged = listSelectedStockCodeRef.current !== nextCode;
    const panelChanged = panelStockCodeRef.current !== nextCode;
    if (!listChanged && !panelChanged) {
      return;
    }
    if (listChanged) {
      setListSelectedStockCode(nextCode);
    }
    if (panelChanged) {
      setPanelStockCode(nextCode);
      setSelectedKlineTime(null);
      if (nextCode) {
        requestStockDetailRef.current(nextCode);
      }
    }
  }, [displayRowCodesKey, pickedFromSearch]);

  const loadChartPanel = useCallback(
    async (code: string) => {
      const c = code.trim();
      const sid = strategyId.trim();
      if (!c || !sid) {
        setKlineBars([]);
        setAssessPoints([]);
        return;
      }
      setPanelLoading(true);
      try {
        const [klines, series] = await Promise.all([
          TacticsApi.listStockDayKlines(spaceId, spaceProductId, c, 300),
          TacticsApi.listDailyStockScores(spaceId, spaceProductId, sid, c),
        ]);
        setKlineBars(klines);
        setAssessPoints(series);
      } catch (e) {
        message.error(e instanceof Error ? e.message : "加载图表失败");
        setKlineBars([]);
        setAssessPoints([]);
      } finally {
        setPanelLoading(false);
      }
    },
    [spaceId, spaceProductId, strategyId]
  );

  useEffect(() => {
    const code = panelStockCode.trim();
    if (!code || !strategyId.trim()) {
      setKlineBars((prev) => (prev.length ? [] : prev));
      setAssessPoints((prev) => (prev.length ? [] : prev));
      return;
    }
    void loadChartPanel(code);
  }, [loadChartPanel, panelStockCode, strategyId]);

  useEffect(() => {
    const sid = strategyId.trim();
    if (!sid) {
      return undefined;
    }
    let alive = true;
    void TacticsApi.getUserSignalSettings(spaceId, spaceProductId, sid).then((row) => {
      if (!alive) return;
      setSignalSaved(row);
    });
    return () => {
      alive = false;
    };
  }, [spaceId, spaceProductId, strategyId]);

  async function loadMarketRows(): Promise<void> {
    const rows = await TacticsApi.listMarketStrategies(spaceId, spaceProductId);
    const sorted = applyStrategiesRecentOrder(Array.isArray(rows) ? rows : []);
    setMarketPick(
      (sorted.find((x) => x.id === strategyId) ?? sorted[0])?.id ?? ""
    );
  }

  const openSignalSettings = (): void => {
    const sid = strategyId.trim();
    if (!sid) {
      message.warning("请先选择策略");
      return;
    }
    setSignalSettingsOpen(true);
    setSignalBusy(true);
    setDraftOppThr(null);
    setDraftRiskThr(null);
    setDraftOppIcon(null);
    setDraftRiskIcon(null);
    void (async () => {
      const opts = await TacticsApi.listSignalIconOptions();
      setSignalOpportunityOpts(opts.opportunity);
      setSignalRiskOpts(opts.risk);
      const saved = await TacticsApi.getUserSignalSettings(spaceId, spaceProductId, sid);
      const draft = readSavedSignalDraft(saved);
      setDraftOppThr(draft.opportunityThreshold);
      setDraftRiskThr(draft.riskThreshold);
      setDraftOppIcon(draft.opportunityIconUrl);
      setDraftRiskIcon(draft.riskIconUrl);
      setSignalBusy(false);
    })();
  };

  const strategySelectOptions = useMemo(() => {
    const opts = dropdownItems.map((s) => {
      const sidePref = normalizeChanceSidePreference(s.chanceSidePreference);
      return {
        value: s.id,
        label: formatStrategySelectLabel(s.name.trim() || s.id, sidePref),
      };
    });
    const sid = strategyId.trim();
    if (sid && !opts.some((o) => String(o.value).trim() === sid)) {
      const extra =
        marketStrategies.find((s) => s.id.trim() === sid) ?? null;
      if (extra) {
        const sidePref = normalizeChanceSidePreference(extra.chanceSidePreference);
        opts.push({
          value: extra.id,
          label: formatStrategySelectLabel(extra.name.trim() || extra.id, sidePref),
        });
      }
    }
    return opts;
  }, [dropdownItems, marketStrategies, strategyId]);

  const openStrategyMarket = (): void => {
    void loadMarketRows();
    setMarketOpen(true);
  };

  const selectedStrategyBrief = useMemo((): TacticsStrategyBriefDTO | null => {
    const sid = strategyId.trim();
    if (!sid) {
      return null;
    }
    return (
      dropdownItems.find((s) => s.id.trim() === sid) ??
      marketStrategies.find((s) => s.id.trim() === sid) ??
      null
    );
  }, [dropdownItems, marketStrategies, strategyId]);

  const selectedStrategyName = selectedStrategyBrief?.name?.trim() || strategyId.trim() || "策略";
  const selectedStrategyIntro = selectedStrategyBrief?.intro?.trim() || "";

  const selectedStrategyType = useMemo((): TacticsStrategyScoreType => {
    return normalizeStrategyScoreType(selectedStrategyBrief?.strategyType);
  }, [selectedStrategyBrief]);

  const supportsOpportunitySignal = strategySupportsOpportunity(selectedStrategyType);
  const supportsRiskSignal = strategySupportsRisk(selectedStrategyType);
  const hasConfiguredSignals = hasConfiguredSignalForStrategy(signalSaved, selectedStrategyType);

  const selectedKlineAssess = useMemo(() => {
    if (selectedKlineTime == null) {
      return null;
    }
    return findTacticsAssessForKlineTime(assessPoints, selectedKlineTime);
  }, [assessPoints, selectedKlineTime]);

  const selectedAssessExplain = useMemo(
    () => buildAssessExplainText(selectedKlineAssess, selectedStrategyType),
    [selectedKlineAssess, selectedStrategyType]
  );

  const showAssessExplainEye =
    debugMode && selectedKlineTime != null && !!selectedAssessExplain?.trim();

  useEffect(() => {
    if (!showAssessExplainEye) {
      setAssessExplainOpen(false);
    }
  }, [showAssessExplainEye]);

  useEffect(() => {
    priceClickCountRef.current = 0;
    if (priceClickTimerRef.current) {
      clearTimeout(priceClickTimerRef.current);
      priceClickTimerRef.current = null;
    }
  }, [panelStockCode]);

  useEffect(
    () => () => {
      if (priceClickTimerRef.current) {
        clearTimeout(priceClickTimerRef.current);
      }
    },
    []
  );

  const onPriceDebugTap = useCallback(() => {
    if (priceClickTimerRef.current) {
      clearTimeout(priceClickTimerRef.current);
    }
    priceClickCountRef.current += 1;
    if (priceClickCountRef.current >= PRICE_DEBUG_CLICKS) {
      priceClickCountRef.current = 0;
      setDebugMode((on) => {
        const next = !on;
        message.success(next ? "已进入调试模式" : "已退出调试模式");
        return next;
      });
      return;
    }
    priceClickTimerRef.current = setTimeout(() => {
      priceClickCountRef.current = 0;
      priceClickTimerRef.current = null;
    }, PRICE_CLICK_RESET_MS);
  }, []);

  const klineScoreHintContent = useMemo((): React.ReactNode => {
    if (selectedKlineTime == null) {
      return "点击/选择 K 线查看分数";
    }
    return buildKlineScoreHintContent(selectedKlineAssess, selectedStrategyType);
  }, [selectedKlineAssess, selectedKlineTime, selectedStrategyType]);

  const onKlineSelect = useCallback((kline: KLineData) => {
    setSelectedKlineTime(kline.time);
  }, []);

  const onKlineDeselect = useCallback(() => {
    setSelectedKlineTime(null);
  }, []);

  const selectedStock = useMemo(
    () =>
      displayRows.find((r) => stockRowKey(r) === panelStockCode.trim()) ??
      null,
    [displayRows, panelStockCode]
  );

  const selectedStockName = useMemo(() => {
    const code = panelStockCode.trim();
    if (!code) {
      return "";
    }
    if (selectedStock?.stockName?.trim()) {
      return selectedStock.stockName.trim();
    }
    if (stockLinkDetail.code === code && stockLinkDetail.data?.stockName?.trim()) {
      return stockLinkDetail.data.stockName.trim();
    }
    const hit = globalModel.basicStocksCatalog.find((s) => s.code.trim() === code);
    return hit?.name?.trim() || code;
  }, [selectedStock, panelStockCode, stockLinkDetail.code, stockLinkDetail.data]);

  const selectedStockPrice = useMemo(() => {
    if (selectedStock) {
      return {
        price: selectedStock.lastPrice,
        change: ratioToStockPricePct(selectedStock.pctChange),
      };
    }
    const code = panelStockCode.trim();
    if (stockLinkDetail.code === code && stockLinkDetail.data) {
      return {
        price: stockLinkDetail.data.latestPrice,
        change: ratioToStockPricePct(stockLinkDetail.data.priceChange),
      };
    }
    return null;
  }, [selectedStock, panelStockCode, stockLinkDetail.code, stockLinkDetail.data]);

  const klineData = useMemo(
    () => buildTacticsKlineData(klineBars, assessPoints, signalSaved),
    [assessPoints, klineBars, signalSaved]
  );

  const chartKey = `${panelStockCode}-${strategyId}-${signalSaved?.opportunityIconUrl ?? ""}-${signalSaved?.riskIconUrl ?? ""}-${signalSaved?.opportunityThreshold ?? ""}-${signalSaved?.riskThreshold ?? ""}`;

  const onPickRow = useCallback(
    (r: TacticsStockScoreRowDTO) => {
      const code = stockRowKey(r);
      setPickedFromSearch(false);
      setListSelectedStockCode(code);
      setPanelStockCode(code);
      setSelectedKlineTime(null);
      requestStockDetailForLink(code);
    },
    [requestStockDetailForLink]
  );

  const onSearchStockSelect = useCallback(
    (stock: StockSearchBrief) => {
      const code = stock.code.trim();
      setPickedFromSearch(true);
      setSearchStockCode(code);
      setPanelStockCode(code);
      setSelectedKlineTime(null);
      requestStockDetailForLink(code);
    },
    [requestStockDetailForLink]
  );

  const confirmMarketPick = (): void => {
    const pick = marketPick.trim();
    if (!pick) {
      message.warning("请选择一个策略");
      return;
    }
    setPickedFromSearch(false);
    setSelectedKlineTime(null);
    setStrategyId(pick);
    setMarketOpen(false);
    noteStrategyVisit(pick);
    message.success("已切换策略");
  };

  const persistSignalSettings = async (): Promise<void> => {
    const sid = strategyId.trim();
    if (!sid) {
      message.warning("请先选择策略");
      return;
    }
    const body: TacticsUserSignalSettingsDTO = {
      opportunityThreshold: supportsOpportunitySignal ? draftOppThr ?? null : null,
      opportunityIconUrl: supportsOpportunitySignal ? draftOppIcon : null,
      riskThreshold: supportsRiskSignal ? draftRiskThr ?? null : null,
      riskIconUrl: supportsRiskSignal ? draftRiskIcon : null,
    };
    setSignalBusy(true);
    try {
      await TacticsApi.saveUserSignalSettings(spaceId, spaceProductId, sid, body);
      setSignalSaved(body);
      setSignalSettingsOpen(false);
      message.success("已保存");
    } catch (e) {
      message.error(e instanceof Error ? e.message : "保存失败");
    } finally {
      setSignalBusy(false);
    }
  };

  const onSearchStockCleared = useCallback(() => {
    setPickedFromSearch(false);
    setSearchStockCode("");
    const listCode = listSelectedStockCodeRef.current.trim();
    setPanelStockCode(listCode);
    setSelectedKlineTime(null);
    if (listCode) {
      requestStockDetailForLink(listCode);
    }
  }, [requestStockDetailForLink]);

  const onConceptSelect = useCallback((c: ConceptSearchBrief) => {
    setTactikConceptCode(c.code);
  }, []);

  const onConceptCleared = useCallback(() => {
    setTactikConceptCode("");
    setConceptRows([]);
  }, []);

  const strategySelectDropdownStyle = useMemo(
    () =>
      ({
        ["--tactics-strategy-select-option-color" as string]:
          TacticsPageUiConfig.strategySelect.optionTextColor,
      }) as React.CSSProperties,
    []
  );

  const pageRootStyle = useMemo(() => tacticsPageCssVars(), []);

  const renderStockRows = useCallback(
    (rows: TacticsStockScoreRowDTO[]): JSX.Element => (
      <div className="tactics-scroll-list">
        {rows.map((r, index) => (
          <TacticStockRow
            key={stockRowKey(r) || `row-${index}`}
            row={r}
            active={
              !pickedFromSearch &&
              !!listSelectedStockCode &&
              stockRowKey(r) === listSelectedStockCode
            }
            stockLinkDetail={stockLinkDetail}
            onPick={() => onPickRow(r)}
            onRequestDetail={requestStockDetailForLink}
            buildStockNotesTabProps={buildStockNotesTabProps}
          />
        ))}
      </div>
    ),
    [
      buildStockNotesTabProps,
      listSelectedStockCode,
      onPickRow,
      pickedFromSearch,
      requestStockDetailForLink,
      stockLinkDetail,
    ]
  );

  const topDataDateDayjs = useMemo(
    () => parseTacticsDataDate(topQueryDate.trim() || topPayload.dataDate),
    [topPayload.dataDate, topQueryDate]
  );

  const leftTabsItems = useMemo(
    () => [
      {
        key: "top",
        label: "高分",
        children: (
          <div className="tactics-tab-pane-inner">
            <div className="tactics-data-date-bar">
              <span className="tactics-data-date-label">数据日期</span>
              <DatePicker
                className="tactics-data-date-picker"
                variant="borderless"
                allowClear={false}
                suffixIcon={null}
                format="M月D日"
                value={topDataDateDayjs}
                onChange={(d) => {
                  if (d) {
                    setTopQueryDate(toTacticsDataDateApi(d));
                  }
                }}
              />
            </div>
            {renderStockRows(displayRows)}
          </div>
        ),
      },
      {
        key: "watchlist",
        label: "自选",
        children: (
          <div className="tactics-tab-pane-inner">{renderStockRows(displayRows)}</div>
        ),
      },
      {
        key: "holdings",
        label: "持仓",
        children: (
          <div className="tactics-tab-pane-inner">{renderStockRows(displayRows)}</div>
        ),
      },
      {
        key: "concept",
        label: "概念",
        children: (
          <div className="tactics-tab-pane-inner">
            <div className="tactics-concept-bar">
              <ConceptDropSelect
                conceptsCatalog={conceptConceptDtos}
                conceptCode={tactikConceptCode}
                placeholder="选择概念后查看成分与分数"
                borderRadius={6}
                backgroundColor={TacticsPageUiConfig.stockSearch.backgroundColor}
                focusBackgroundColor={TacticsPageUiConfig.stockSearch.focusBackgroundColor}
                onSelect={onConceptSelect}
                onCleared={onConceptCleared}
              />
            </div>
            {tactikConceptCode.trim() ? renderStockRows(displayRows) : null}
          </div>
        ),
      },
    ],
    [
      conceptConceptDtos,
      displayRows,
      onConceptCleared,
      onConceptSelect,
      renderStockRows,
      tactikConceptCode,
      topDataDateDayjs,
    ]
  );

  return (
    <div className="tactics-root" style={pageRootStyle}>
      <div className="tactics-main-split">
        <div className="tactics-col-left">
          {strategyLoading ? (
            <Spin className="tactics-strategy-loading" />
          ) : (
            <Select
              variant="borderless"
              popupMatchSelectWidth={false}
              className="tactics-strategy-select"
              popupClassName="tactics-strategy-select-popup"
              dropdownStyle={strategySelectDropdownStyle}
              suffixIcon={<DownOutlined className="tactics-select-suffix-icon" />}
              value={strategyId || undefined}
              options={strategySelectOptions}
              placeholder="请选择策略"
              onChange={(v) => {
                const sid = String(v);
                setPickedFromSearch(false);
                setSelectedKlineTime(null);
                setStrategyId(sid);
                noteStrategyVisit(sid);
              }}
              dropdownRender={(menu) => (
                <div className="tactics-strategy-select-dropdown-inner">
                  <div className="tactics-strategy-select-options-wrap">{menu}</div>
                  <div className="tactics-strategy-select-footer-divider" />
                  <div
                    className="tactics-strategy-select-view-all"
                    role="button"
                    tabIndex={0}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={openStrategyMarket}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openStrategyMarket();
                      }
                    }}
                  >
                    <span>查看全部策略</span>
                    <RightOutlined className="tactics-strategy-select-view-all-icon" />
                  </div>
                </div>
              )}
            />
          )}
          <div
            className="tactics-stock-search-bar"
            style={{ width: tacticsWidthCss(TacticsPageUiConfig.stockSearch.width) }}
          >
            <SearchStockWithCatalog
              stockCode={searchStockCode || undefined}
              placeholder="输入股票名称或代码"
              borderRadius={6} 
              backgroundColor={TacticsPageUiConfig.stockSearch.backgroundColor}
              focusBackgroundColor={TacticsPageUiConfig.stockSearch.focusBackgroundColor}
              onSelect={onSearchStockSelect}
              onCleared={onSearchStockCleared}
            />
          </div>
          <div className="tactics-left-tabs-wrap">
            <Spin spinning={listLoading}>
              <Tabs
                activeKey={leftTab}
                onChange={(k) => {
                  setPickedFromSearch(false);
                  setLeftTab(k as typeof leftTab);
                }}
                className="tactics-left-tabs"
                indicator={leftTabsIndicator}
                items={leftTabsItems}
              />
            </Spin>
          </div>
        </div>
        <div className="tactics-col-right">
          <div className="tactics-right-head">
            <div className="tactics-stock-head-line">
              {panelStockCode.trim() ? (
                <div className="tactics-stock-title-row">
                  <StockLink
                    stockCode={panelStockCode.trim()}
                    stockName={selectedStockName || panelStockCode.trim()}
                    stockDetail={
                      stockLinkDetail.code === panelStockCode.trim()
                        ? stockLinkDetail.data
                        : null
                    }
                    detailLoading={
                      stockLinkDetail.code === panelStockCode.trim()
                        ? stockLinkDetail.loading
                        : false
                    }
                    {...stockLinkDetailPropsForCode(stockLinkDetail, panelStockCode.trim())}
                    onRequestDetail={requestStockDetailForLink}
                    stockNotesTab={buildStockNotesTabProps(
                      panelStockCode.trim(),
                      selectedStockName || panelStockCode.trim()
                    )}
                  />
                  {selectedStockPrice ? (
                    <div
                      className="tactics-stock-price-hit"
                      onClick={onPriceDebugTap}
                      role="presentation"
                    >
                      <StockPrice
                        mode="one-line"
                        price={selectedStockPrice.price}
                        change={selectedStockPrice.change}
                      />
                    </div>
                  ) : null}
                </div>
              ) : (
                <span className="tactics-muted-note">请先选择一只股票</span>
              )}
              {selectedStrategyIntro || selectedStrategyName ? (
                <div className="tactics-strategy-intro">
                  <span className="tactics-strategy-intro-name">{selectedStrategyName}</span>
                  {selectedStrategyIntro ? (
                    <span className="tactics-strategy-intro-text">
                      {selectedStrategyIntro}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="tactics-signal-head">
              {hasConfiguredSignals ? (
                <>
                  {supportsOpportunitySignal && signalSaved?.opportunityIconUrl ? (
                    <span className="tactics-signal-inline">
                      <img
                        src={signalSaved.opportunityIconUrl}
                        className="tactics-signal-inline-icon"
                        alt=""
                      />
                      <span>
                        {formatSignalLegendLabel("机会", signalSaved.opportunityThreshold)}
                      </span>
                    </span>
                  ) : null}
                  {supportsRiskSignal && signalSaved?.riskIconUrl ? (
                    <span className="tactics-signal-inline">
                      <img
                        src={signalSaved.riskIconUrl}
                        className="tactics-signal-inline-icon"
                        alt=""
                      />
                      <span>
                        {formatSignalLegendLabel("风险", signalSaved.riskThreshold)}
                      </span>
                    </span>
                  ) : null}
                  <Button
                    type="text"
                    icon={<SettingOutlined />}
                    aria-label="设置信号"
                    onClick={() => openSignalSettings()}
                  />
                </>
              ) : (
                <>
                  <span className="tactics-muted-note">您尚未设置信号</span>
                  <Button
                    type="text"
                    icon={<SettingOutlined />}
                    aria-label="设置信号"
                    onClick={() => openSignalSettings()}
                  />
                </>
              )}
            </div>
          </div>
          <div className="tactics-chart-hint-bar">
            <span className="tactics-chart-hint-left">
              {hasConfiguredSignals
                ? "图表中超过阈值的技术信号基于您的自定义设置，仅供参考，不应作为交易决策的唯一依据。"
                : "策略技术得分为机械式计算结果，仅供参考，严禁作为交易决策的唯一依据。"}
            </span>
            <span className="tactics-chart-hint-right">
              {showAssessExplainEye ? (
                <Button
                  type="text"
                  className="tactics-assess-explain-eye"
                  icon={<EyeOutlined />}
                  aria-label="查看评估说明"
                  onClick={() => setAssessExplainOpen(true)}
                />
              ) : null}
              {klineScoreHintContent}
            </span>
          </div>
          <div className="tactics-chart-section">
            <div className="tactics-chart-wrap" ref={chartRef}>
              <Spin spinning={panelLoading}>
                {chartWidth > 0 &&
                chartHeight > 0 &&
                panelStockCode.trim() &&
                klineData.length > 0 ? (
                  <div className="tactics-kline-chart">
                    <KChart
                      enablePriceHint
                      key={chartKey}
                      width={chartWidth}
                      height={chartHeight}
                      klineData={klineData}
                      fontSize={8}
                      gridUnit="month"
                      maTypes={[...TacticsPageUiConfig.klineMa.types]}
                      maColors={[...TacticsPageUiConfig.klineMa.colors]}
                      defaultVisibleMaTypes={[
                        ...TacticsPageUiConfig.klineMa.defaultVisibleTypes,
                      ]}
                      upColor="#DE554F"
                      downColor="#51A679"
                      backgroundColor="#FFFFFF"
                      gridColor="#F3F3F3"
                      gridType="solid"
                      labelColor="#545454"
                      valueHintColor="#545454"
                      indicateLineColor="#999999"
                      indicatorBgColor="#333333"
                      indicatorColor="#FFFFFF"
                      verticalFlex={[13, 4]}
                      defaultValueLineColor="#427BF6"
                      kBarType={["solid", "hollow"]}
                      klineWidth={{
                        start: 5,
                        min: 2,
                        max: 16,
                        lineWidth: 1,
                        minGap: 0.1,
                        maxGap: 2,
                      }}
                      zoom={{
                        minScale: 0.2,
                        maxScale: 6,
                      }}
                      volumeBar={{
                        field: "volume",
                        height: 80,
                        upColor: "#DE554F",
                        downColor: "#51A679",
                      }}
                      markGap={5}
                      onSelect={onKlineSelect}
                      onDeselect={onKlineDeselect}
                    />
                  </div>
                ) : (
                  <div className="tactics-chart-empty">
                    {panelStockCode.trim()
                      ? "暂无 K 线数据"
                      : "请从左侧列表或搜索框选择股票"}
                  </div>
                )}
              </Spin>
            </div>
          </div>
        </div>
      </div>

      <Modal
        title="策略市场"
        open={marketOpen}
        onCancel={() => setMarketOpen(false)}
        footer={[
          <Button key="cancel" className={RECT_BTN} onClick={() => setMarketOpen(false)}>
            取消
          </Button>,
          <Button
            key="ok"
            type="primary"
            className={RECT_BTN}
            onClick={confirmMarketPick}
          >
            确认切换
          </Button>,
        ]}
        width={720}
        className="tactics-market-modal"
        destroyOnHidden
      >
        <div className="tactics-market-list">
          {marketStrategies.map((s) => {
            const picked = marketPick === s.id;
            const scoreType = normalizeStrategyScoreType(s.strategyType);
            const typeLabel =
              scoreType === "RISK" ? "风险" : scoreType === "ALL" ? "机会+风险" : "机会";
            return (
              <button
                key={s.id}
                type="button"
                className={
                  picked
                    ? "tactics-market-card tactics-market-card--picked"
                    : "tactics-market-card"
                }
                onClick={() => setMarketPick(s.id)}
              >
                <div className="tactics-market-card-main">
                  <div className="tactics-market-card-title-row">
                    <span className="tactics-market-card-name">{s.name}</span>
                    <span
                      className={`tactics-market-card-tag tactics-market-card-tag--${scoreType.toLowerCase()}`}
                    >
                      {typeLabel}
                    </span>
                  </div>
                  <div className="tactics-market-card-intro">{s.intro}</div>
                </div>
                <span
                  className={
                    picked
                      ? "tactics-market-card-check tactics-market-card-check--on"
                      : "tactics-market-card-check"
                  }
                  aria-hidden
                />
              </button>
            );
          })}
        </div>
      </Modal>

      <Modal
        title="评估说明"
        open={assessExplainOpen}
        onCancel={() => setAssessExplainOpen(false)}
        footer={[
          <Button
            key="ok"
            type="primary"
            className={RECT_BTN}
            onClick={() => setAssessExplainOpen(false)}
          >
            关闭
          </Button>,
        ]}
        width={560}
        className="tactics-assess-explain-modal"
        destroyOnHidden
      >
        <pre className="tactics-assess-explain-body">
          {selectedAssessExplain || "暂无说明"}
        </pre>
      </Modal>

      <Modal
        title={`${selectedStrategyName}信号设置`}
        open={signalSettingsOpen}
        onCancel={() => setSignalSettingsOpen(false)}
        footer={[
          <Button key="cancel" className={RECT_BTN} onClick={() => setSignalSettingsOpen(false)}>
            取消
          </Button>,
          <Button
            key="ok"
            type="primary"
            className={RECT_BTN}
            loading={signalBusy}
            onClick={() => void persistSignalSettings()}
          >
            保存
          </Button>,
        ]}
        width={640}
        className="tactics-signal-modal"
        destroyOnHidden
      >
        <Spin spinning={signalBusy}>
          <div
            className={
              supportsOpportunitySignal
                ? "tactics-signal-block"
                : "tactics-signal-block tactics-signal-block--disabled"
            }
          >
            <div className="tactics-signal-row">
              <div className="tactics-signal-row-label">
                <span>
                  机会出现阈值设置
                  <Tooltip title="设置阈值后，超过此阈值的日线上方会出现图形标记，更加形象化。">
                    <QuestionCircleOutlined className="tactics-q-icon" />
                  </Tooltip>
                </span>
                <InputNumber
                  min={0}
                  max={100}
                  disabled={!supportsOpportunitySignal}
                  className="tactics-input-flat"
                  value={draftOppThr ?? undefined}
                  onChange={(v) => setDraftOppThr(typeof v === "number" ? v : null)}
                />
              </div>
              <div className="tactics-signal-row-icons">
                <div className="tactics-icon-pick-grid">
                  {signalOpportunityOpts.map((opt, index) => (
                    <button
                      key={opt.code || opt.imageUrl || `opp-${index}`}
                      type="button"
                      disabled={!supportsOpportunitySignal}
                      className={
                        supportsOpportunitySignal && draftOppIcon === opt.imageUrl
                          ? "tactics-icon-pick tactics-icon-pick--on"
                          : "tactics-icon-pick"
                      }
                      onClick={() => setDraftOppIcon(opt.imageUrl)}
                    >
                      <img src={opt.imageUrl} alt="" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {!supportsOpportunitySignal ? (
              <div className="tactics-signal-unsupported">该策略暂不支持机会图标设置</div>
            ) : null}
          </div>
          <div
            className={
              supportsRiskSignal
                ? "tactics-signal-block"
                : "tactics-signal-block tactics-signal-block--disabled"
            }
          >
            <div className="tactics-signal-row">
              <div className="tactics-signal-row-label">
                <span>
                  风险出现阈值设置
                  <Tooltip title="设置阈值后，超过此阈值的日线上方会出现图形标记，更加形象化。">
                    <QuestionCircleOutlined className="tactics-q-icon" />
                  </Tooltip>
                </span>
                <InputNumber
                  min={0}
                  max={100}
                  disabled={!supportsRiskSignal}
                  className="tactics-input-flat"
                  value={draftRiskThr ?? undefined}
                  onChange={(v) => setDraftRiskThr(typeof v === "number" ? v : null)}
                />
              </div>
              <div className="tactics-signal-row-icons">
                <div className="tactics-icon-pick-grid">
                  {signalRiskOpts.map((opt, index) => (
                    <button
                      key={opt.code || opt.imageUrl || `risk-${index}`}
                      type="button"
                      disabled={!supportsRiskSignal}
                      className={
                        supportsRiskSignal && draftRiskIcon === opt.imageUrl
                          ? "tactics-icon-pick tactics-icon-pick--on"
                          : "tactics-icon-pick"
                      }
                      onClick={() => setDraftRiskIcon(opt.imageUrl)}
                    >
                      <img src={opt.imageUrl} alt="" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {!supportsRiskSignal ? (
              <div className="tactics-signal-unsupported">该策略暂不支持风险图标设置</div>
            ) : null}
          </div>
        </Spin>
      </Modal>
    </div>
  );
};

export default observer(TacticsPage);

interface RowProps {
  row: TacticsStockScoreRowDTO;
  active: boolean;
  stockLinkDetail: StockLinkDetailState;
  onPick: () => void;
  onRequestDetail: (code: string) => void;
  buildStockNotesTabProps: (stockCode: string, stockName?: string) => StockNotesTabProps | null;
}

function TacticStockRow(p: RowProps): JSX.Element {
  const code = stockRowKey(p.row);
  return (
    <div
      role="button"
      tabIndex={0}
      className={
        p.active ? "tactics-stock-row tactics-stock-row--active" : "tactics-stock-row"
      }
      onClick={p.onPick}
      onKeyDown={(ev) => {
        if (ev.key === "Enter" || ev.key === " ") {
          ev.preventDefault();
          p.onPick();
        }
      }}
    >
      <div className="tactics-stock-row-left">
        <StockLink
          stockCode={code}
          stockName={stockRowDisplayName(p.row)}
          stockDetail={p.stockLinkDetail.code === code ? p.stockLinkDetail.data : null}
          detailLoading={p.stockLinkDetail.code === code ? p.stockLinkDetail.loading : false}
          {...stockLinkDetailPropsForCode(p.stockLinkDetail, code)}
          onRequestDetail={p.onRequestDetail}
          stockNotesTab={p.buildStockNotesTabProps(code, stockRowDisplayName(p.row))}
        />
        <StockPrice
          mode="one-line"
          price={p.row.lastPrice}
          change={ratioToStockPricePct(p.row.pctChange)}
          fontSize={TacticsPageUiConfig.stockListRow.stockPriceFontSize}
        />
      </div>
      <span className="tactics-stock-score">{formatTacticsScoreValue(p.row.score)}</span>
    </div>
  );
}
